import React from 'react';
import { createGate, useGate } from 'effector-react';
import { ImageType } from '@types';
import addComponent from '@utils/addComponent';
import Image from '@components/Image';
import Button from '@components/Button';
import { Backdrop } from '@components/Modal/modalParts';
import { $tourDataApi } from '@stores/dataStores';
import { $editingImageMarkerApi } from '@stores/editingMarkerStores';
import { $stImageSettingPanelApi } from '@stores/sandTableStores';
import domain from '../domain';
import { isSTImageOrMatrix } from '../utils/sandTableTypeCheck';
import { stEditorKrpPlayer } from '../STKrpPlayer';

interface Props {
  defaultAngle?: number;
  onConfirm: (angle: number) => void;
}

export const showCompassSetting = ({
  sandTableId,
  angle,
}: {
  sandTableId: string;
  angle?: number;
}) => {
  $editingImageMarkerApi.clear();
  $stImageSettingPanelApi.off();
  addComponent((remove) => {
    return (
      <CompassSetting
        defaultAngle={angle}
        onConfirm={(newAngle) => {
          $tourDataApi.updateSandtable({
            id: sandTableId,
            updater: (draft) => {
              if (isSTImageOrMatrix(draft)) {
                draft.angle = newAngle;
              }
              stEditorKrpPlayer.setNorthAngle(newAngle);
            },
          });
          remove();
        }}
      />
    );
  });
};

export const $GateCompassSetting = createGate<boolean>({
  defaultState: false,
  domain,
});

export function CompassSetting({ defaultAngle = 0, onConfirm }: Props) {
  const [angle, setAngle] = React.useState<number>(defaultAngle);

  useGate($GateCompassSetting, true);

  const handleMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    const node = e.target as HTMLElement;
    const handleMouseMove = (moveE: MouseEvent) => {
      const { top, left, width, height } = node.getBoundingClientRect();
      const centerX = left + width / 2;
      const centerY = top + height / 2;
      setAngle(
        Math.atan2(moveE.pageX - centerX, centerY - moveE.pageY) *
          (180 / Math.PI)
      );
    };
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
  };

  return (
    <Backdrop
      style={{ zIndex: 12000, paddingRight: 250 }}
      className="flex items-center justify-center text-base text-white"
    >
      <div className="absolute flex select-none flex-col items-center justify-center rounded bg-black-black bg-opacity-50 p-30">
        <div className="text-xl">拖动指北针为沙盘设置正北方向</div>
        <div className="mb-40">以便雷达与全景视角进行匹配</div>
        <Image
          src="/imgs/compass.6edea652d6d03c85dd104f5b09a1dbf2.png"
          draggable={false}
          type={ImageType.sysMedia}
          className="cursor-ew-resize"
          style={{
            width: 250,
            height: 250,
            transform: `rotate(${angle}deg)`,
          }}
          onMouseDown={handleMouseDown}
        />
        <Button
          className="mt-30"
          onClick={() => onConfirm(Number(angle.toFixed(2)))}
        >
          完成设置
        </Button>
      </div>
    </Backdrop>
  );
}
